<template>
  <AdminLayout>
    <div class="space-y-8">
      <!-- Page Header -->
      <div class="mb-8">
        <h1 class="text-3xl font-bold text-white mb-2">Job Management</h1>
        <p class="text-slate-400">Monitor and manage all job postings and applications</p>
      </div>

      <!-- Stats Cards -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div class="admin-stat-card">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <p class="text-sm font-medium text-slate-400 mb-2">Active Jobs</p>
              <p class="text-3xl font-bold text-white">{{ stats.activeJobs }}</p>
              <p class="text-sm text-emerald-400 mt-2">Currently open positions</p>
            </div>
            <div class="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
              <svg class="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2V6"/>
              </svg>
            </div>
          </div>
        </div>

        <div class="admin-stat-card">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <p class="text-sm font-medium text-slate-400 mb-2">Total Applications</p>
              <p class="text-3xl font-bold text-white">{{ Number(stats.totalApplications || 0).toLocaleString() }}</p>
              <p class="text-sm text-emerald-400 mt-2">
                <span class="inline-flex items-center">
                  <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 10l7-7m0 0l7 7m-7-7v18"/>
                  </svg>
                  +15% this week
                </span>
              </p>
            </div>
            <div class="w-12 h-12 bg-cyan-500/20 rounded-xl flex items-center justify-center">
              <svg class="w-6 h-6 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
            </div>
          </div>
        </div>

        <div class="admin-stat-card">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <p class="text-sm font-medium text-slate-400 mb-2">Filled Positions</p>
              <p class="text-3xl font-bold text-white">{{ stats.filledPositions }}</p>
              <p class="text-sm text-emerald-400 mt-2">This month</p>
            </div>
            <div class="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
              <svg class="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
          </div>
        </div>

        <div class="admin-stat-card">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <p class="text-sm font-medium text-slate-400 mb-2">Avg. Time to Fill</p>
              <p class="text-3xl font-bold text-white">{{ stats.avgTimeToFill }}</p>
              <p class="text-sm text-slate-400 mt-2">Days to hire</p>
            </div>
            <div class="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
              <svg class="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
          </div>
        </div>
      </div>

      <!-- Actions Bar -->
      <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div class="flex items-center space-x-4">
          <div class="relative">
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search jobs..."
              class="w-64 pl-10 pr-4 py-2 bg-slate-800/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            >
            <svg class="absolute left-3 top-2.5 w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
          </div>
          <select
            v-model="filterStatus"
            class="px-4 py-2 bg-slate-800/50 border border-slate-600/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="filled">Filled</option>
            <option value="expired">Expired</option>
          </select>
          <select
            v-model="filterCategory"
            class="px-4 py-2 bg-slate-800/50 border border-slate-600/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          >
            <option value="">All Categories</option>
            <option value="drilling">Drilling</option>
            <option value="production">Production</option>
            <option value="maintenance">Maintenance</option>
            <option value="safety">Safety</option>
            <option value="management">Management</option>
          </select>
        </div>
        <div class="flex items-center space-x-3">
          <button
            @click="exportJobs"
            class="px-4 py-2 bg-slate-700/50 border border-slate-600/50 text-slate-300 hover:text-white hover:border-emerald-500/30 rounded-lg transition-all duration-300"
          >
            <svg class="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
            </svg>
            Export
          </button>
          <button
            @click="showCreateModal = true"
            class="btn-hero-primary"
          >
            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
            </svg>
            Post Job
          </button>
        </div>
      </div>

      <!-- Jobs Table -->
      <div class="admin-stat-card overflow-hidden">
        <div class="overflow-x-auto">
          <table class="w-full">
            <thead>
              <tr class="border-b border-slate-700/50">
                <th class="text-left py-4 px-6 text-sm font-semibold text-slate-300">Job Title</th>
                <th class="text-left py-4 px-6 text-sm font-semibold text-slate-300">Company</th>
                <th class="text-left py-4 px-6 text-sm font-semibold text-slate-300">Category</th>
                <th class="text-left py-4 px-6 text-sm font-semibold text-slate-300">Applications</th>
                <th class="text-left py-4 px-6 text-sm font-semibold text-slate-300">Status</th>
                <th class="text-left py-4 px-6 text-sm font-semibold text-slate-300">Posted</th>
                <th class="text-left py-4 px-6 text-sm font-semibold text-slate-300">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="job in filteredJobs" :key="job.id" class="border-b border-slate-700/30 hover:bg-slate-800/20 transition-colors">
                <td class="py-4 px-6">
                  <div>
                    <p class="font-semibold text-white">{{ job.title }}</p>
                    <p class="text-sm text-slate-400">{{ job.location }}</p>
                  </div>
                </td>
                <td class="py-4 px-6">
                  <div class="flex items-center space-x-2">
                    <div class="w-8 h-8 bg-gradient-to-br from-emerald-400 to-cyan-400 rounded-lg flex items-center justify-center text-slate-900 font-semibold text-sm">
                      {{ job.company.charAt(0) }}
                    </div>
                    <span class="text-slate-300">{{ job.company }}</span>
                  </div>
                </td>
                <td class="py-4 px-6">
                  <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-700/50 text-slate-300">
                    {{ job.category }}
                  </span>
                </td>
                <td class="py-4 px-6">
                  <div class="flex items-center space-x-2">
                    <span class="text-white font-semibold">{{ job.applications }}</span>
                    <span class="text-slate-400 text-sm">applicants</span>
                  </div>
                </td>
                <td class="py-4 px-6">
                  <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                        :class="{
                          'bg-emerald-100 text-emerald-800': job.status === 'active',
                          'bg-yellow-100 text-yellow-800': job.status === 'paused',
                          'bg-blue-100 text-blue-800': job.status === 'filled',
                          'bg-red-100 text-red-800': job.status === 'expired'
                        }">
                    {{ job.status.charAt(0).toUpperCase() + job.status.slice(1) }}
                  </span>
                </td>
                <td class="py-4 px-6 text-slate-300">{{ formatDate(job.postedDate) }}</td>
                <td class="py-4 px-6">
                  <div class="flex items-center space-x-2">
                    <button
                      @click="viewJob(job)"
                      class="p-2 text-slate-400 hover:text-emerald-400 transition-colors"
                      title="View Details"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                      </svg>
                    </button>
                    <button
                      @click="editJob(job)"
                      class="p-2 text-slate-400 hover:text-cyan-400 transition-colors"
                      title="Edit Job"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                      </svg>
                    </button>
                    <button
                      @click="toggleJobStatus(job)"
                      class="p-2 text-slate-400 hover:text-yellow-400 transition-colors"
                      :title="job.status === 'active' ? 'Pause Job' : 'Activate Job'"
                    >
                      <svg v-if="job.status === 'active'" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                      <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                    </button>
                    <button
                      @click="deleteJob(job.id)"
                      class="p-2 text-slate-400 hover:text-red-400 transition-colors"
                      title="Delete Job"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1-1H8a1 1 0 00-1 1v3M4 7h16"/>
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Create Job Modal -->
    <div v-if="showCreateModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div class="bg-slate-800/90 backdrop-blur-xl rounded-2xl p-6 w-full max-w-2xl border border-slate-700/50 max-h-[90vh] overflow-y-auto">
        <h3 class="text-xl font-bold text-white mb-4">Post New Job</h3>
        <form @submit.prevent="createJob" class="space-y-4">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Job Title</label>
              <input
                v-model="newJob.title"
                type="text"
                required
                class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="Enter job title"
              >
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Company</label>
              <input
                v-model="newJob.company"
                type="text"
                required
                class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="Company name"
              >
            </div>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Category</label>
              <select
                v-model="newJob.category"
                class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                <option value="drilling">Drilling</option>
                <option value="production">Production</option>
                <option value="maintenance">Maintenance</option>
                <option value="safety">Safety</option>
                <option value="management">Management</option>
              </select>
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Location</label>
              <input
                v-model="newJob.location"
                type="text"
                required
                class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="Job location"
              >
            </div>
          </div>
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Description</label>
            <textarea
              v-model="newJob.description"
              rows="4"
              class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-y"
              placeholder="Job description and requirements"
            ></textarea>
          </div>
          <div class="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              @click="showCreateModal = false"
              class="px-4 py-2 text-slate-400 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="btn-hero-primary"
            >
              Post Job
            </button>
          </div>
        </form>
      </div>
    </div>
  </AdminLayout>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useAdminStore } from '../../stores/adminStore'
import AdminLayout from './AdminLayout.vue'

const adminStore = useAdminStore()

// State
const stats = ref({
  activeJobs: 0,
  totalApplications: 0,
  filledPositions: 0,
  avgTimeToFill: 0
})

const jobs = ref<any[]>([])
const isLoading = ref(false)

// Reactive state
const searchQuery = ref('')
const filterStatus = ref('')
const filterCategory = ref('')
const showCreateModal = ref(false)
const newJob = ref({
  title: '',
  company: '',
  category: 'drilling',
  location: '',
  description: ''
})

// Computed
const filteredJobs = computed(() => {
  let filtered = jobs.value

  if (searchQuery.value) {
    const q = searchQuery.value.toLowerCase()
    filtered = filtered.filter(job =>
      String(job.title || '').toLowerCase().includes(q) ||
      String(job.company || '').toLowerCase().includes(q) ||
      String(job.location || '').toLowerCase().includes(q)
    )
  }

  if (filterStatus.value) {
    filtered = filtered.filter(job => job.status === filterStatus.value)
  }

  if (filterCategory.value) {
    filtered = filtered.filter(job => job.category === filterCategory.value)
  }

  return filtered
})

// Methods
const formatDate = (date: Date | string) => {
  const parsed = date instanceof Date ? date : new Date(date)
  if (Number.isNaN(parsed.getTime())) return '—'
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  }).format(parsed)
}

const viewJob = (job: any) => {
  // TODO: Implement view job details
  console.log('View job:', job)
}

const editJob = (job: any) => {
  // TODO: Implement edit job functionality
  console.log('Edit job:', job)
}

const toggleJobStatus = (job: any) => {
  job.status = job.status === 'active' ? 'paused' : 'active'
}

const deleteJob = (jobId: number) => {
  if (confirm('Are you sure you want to delete this job posting?')) {
    jobs.value = jobs.value.filter(job => job.id !== jobId)
  }
}

const createJob = () => {
  const job = {
    id: jobs.value.length + 1,
    ...newJob.value,
    applications: 0,
    status: 'active',
    postedDate: new Date()
  }
  jobs.value.push(job)
  showCreateModal.value = false
  newJob.value = { title: '', company: '', category: 'drilling', location: '', description: '' }
}

const loadJobs = async () => {
  try {
    isLoading.value = true
    const response = await fetch('/api/admin/jobs', {
      headers: {
        'Authorization': `Bearer ${adminStore.adminToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      const rows = Array.isArray(data.jobs) ? data.jobs : []
      jobs.value = rows.map((job: any) => ({
        ...job,
        title: job.title || 'Untitled job',
        company: job.company || '',
        category: job.category || job.type || '',
        location: job.location || '',
        applications: Number(job.applications || 0),
        status: job.status || 'active',
        postedDate: job.postedDate || job.createdAt || new Date(),
      }))
      stats.value = {
        activeJobs: Number(data.stats?.activeJobs ?? jobs.value.filter((j: any) => j.status === 'active').length),
        totalApplications: Number(data.stats?.totalApplications ?? jobs.value.reduce((sum: number, j: any) => sum + Number(j.applications || 0), 0)),
        filledPositions: Number(data.stats?.filledPositions ?? jobs.value.filter((j: any) => j.status === 'filled').length),
        avgTimeToFill: Number(data.stats?.avgTimeToFill ?? 0),
      }
    } else {
      // Load mock data for demo
      loadMockData()
    }
  } catch (error) {
    console.error('Error loading jobs:', error)
    loadMockData()
  } finally {
    isLoading.value = false
  }
}

const loadMockData = () => {
  jobs.value = [
    {
      id: 1,
      title: 'Senior Drilling Engineer',
      company: 'Apex Energy',
      category: 'drilling',
      location: 'Houston, TX',
      applications: 24,
      status: 'active',
      postedDate: new Date('2024-01-10'),
      description: 'Lead drilling operations for offshore projects...'
    },
    {
      id: 2,
      title: 'Production Supervisor',
      company: 'Gulf Coast Oil',
      category: 'production',
      location: 'Louisiana',
      applications: 18,
      status: 'active',
      postedDate: new Date('2024-01-12'),
      description: 'Oversee daily production activities...'
    },
    {
      id: 3,
      title: 'Safety Coordinator',
      company: 'Titan Drilling',
      category: 'safety',
      location: 'North Dakota',
      applications: 31,
      status: 'filled',
      postedDate: new Date('2024-01-05'),
      description: 'Implement safety protocols and training...'
    },
    {
      id: 4,
      title: 'Maintenance Technician',
      company: 'Precision Rigs',
      category: 'maintenance',
      location: 'Oklahoma',
      applications: 12,
      status: 'paused',
      postedDate: new Date('2024-01-08'),
      description: 'Maintain and repair drilling equipment...'
    },
    {
      id: 5,
      title: 'Operations Manager',
      company: 'Summit Energy',
      category: 'management',
      location: 'Texas',
      applications: 8,
      status: 'expired',
      postedDate: new Date('2023-12-20'),
      description: 'Manage field operations and personnel...'
    }
  ]
  
  stats.value = {
    activeJobs: 156,
    totalApplications: 2847,
    filledPositions: 89,
    avgTimeToFill: 12
  }
}

const exportJobs = async () => {
  try {
    const response = await fetch('/api/admin/jobs/export', {
      headers: {
        'Authorization': `Bearer ${adminStore.adminToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `jobs-export-${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    }
  } catch (error) {
    console.error('Error exporting jobs:', error)
  }
}

onMounted(() => {
  loadJobs()
})
</script>

<style scoped>
.admin-stat-card {
  @apply bg-slate-800/40 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 hover:border-emerald-500/30 transition-all duration-300;
}

.btn-hero-primary {
  @apply inline-flex items-center px-4 py-2 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-semibold rounded-lg hover:from-emerald-600 hover:to-cyan-600 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-emerald-500/25;
}
</style>
